import { ref, ComputedRef, computed, Ref, watch, nextTick } from 'vue'

import { isEmojiWithoutTextCheck } from '@/shared/utils/emoji'
import { decodeHtmlEntities } from '@/shared/utils/htmlEntitiesDecode'
import { sanitizeToText } from '@/shared/utils/sanitize'

import { LINES_COUNT_TO_CLAMP_TEXT_DEFAULT } from './contants'

export const useTextExpand = (text: ComputedRef<string | undefined> | Ref<string | undefined>, linesCount = LINES_COUNT_TO_CLAMP_TEXT_DEFAULT) => {
  const textElement = ref<HTMLElement | null>(null)
  const isTextTruncated = ref(false)
  const isExpanded = ref(false)

  const initTextExpanding = () => {
    if (!textElement.value) return

    isExpanded.value = false

    if (isEmojiOnly.value) {
      textElement.value.classList.remove('truncate')
      textElement.value.classList.remove(`line-clamp-${linesCount}`)
      textElement.value.classList.remove('wrap-anywhere')
      textElement.value.classList.remove('break-words')
      textElement.value.classList.add('whitespace-pre-wrap')
      isTextTruncated.value = false

      return
    }

    textElement.value.classList.add('truncate')
    textElement.value.classList.add(`line-clamp-${linesCount}`)
    textElement.value?.classList.remove('wrap-anywhere')
    textElement.value?.classList.add('whitespace-pre-wrap')

    isTextTruncated.value = textElement.value.scrollWidth > textElement.value.clientWidth || textElement.value.scrollHeight > textElement.value.clientHeight

    if (linesCount && linesCount > 1) {
      textElement.value.classList.remove('truncate')
    }
  }

  const sanitizedText = computed(() => {
    return decodeHtmlEntities(sanitizeToText(text.value ?? ''))
  })

  const isEmojiOnly = computed(() => {
    const normalizedText = sanitizedText.value.trim()
    return normalizedText.length > 0 && isEmojiWithoutTextCheck(normalizedText)
  })

  const toggleExpand = (e: MouseEvent) => {
    e.stopPropagation()

    isExpanded.value = !isExpanded.value

    if (!linesCount || linesCount === 1) {
      textElement.value?.classList.toggle('truncate')
    }

    textElement.value?.classList.toggle(`line-clamp-${linesCount}`)
    textElement.value?.classList.toggle('wrap-anywhere')
    textElement.value?.classList.toggle('break-words')
  }

  watch(text, async (n, o) => {
    if (n !== o) {
      await nextTick()

      initTextExpanding()
    }
  })

  return {
    textElement,
    isTextTruncated,
    isExpanded,
    sanitizedText,
    toggleExpand,
    initTextExpanding,
  }
}
